import { useState, useEffect, useCallback } from 'react';
import AdminAuthGate from '@/components/AdminAuthGate';
import PlaceDetailDrawer from '@/components/suggestions/PlaceDetailDrawer';
import ApproveModal, { type ApprovePayload } from '@/components/suggestions/ApproveModal';
import { toast } from 'sonner';
import { Loader2, X, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_BASE = 'https://spotsvc.up.railway.app';
const PAGE_SIZE = 20;

type Status = 'pending' | 'approved' | 'rejected';
type TabFilter = 'all' | Status;

interface Suggestion {
  id: string;
  google_place_id: string;
  place_name: string;
  place_address: string | null;
  suggester_name: string | null;
  suggester_email: string | null;
  note: string | null;
  status: Status;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface FetchResult {
  suggestions: Suggestion[];
  total: number;
  page: number;
  page_size: number;
}

const TABS: { label: string; value: TabFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

function relativeTime(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function StatusBadge({ status }: { status: Status }) {
  return (
    <span
      className={cn(
        'inline-block text-xs font-semibold px-2.5 py-1 rounded-full capitalize',
        status === 'pending' && 'bg-warning/15 text-warning',
        status === 'approved' && 'bg-success/15 text-success',
        status === 'rejected' && 'bg-destructive/15 text-destructive',
      )}
    >
      {status}
    </span>
  );
}

// ── Modal ──
function ActionModal({
  heading,
  confirmLabel,
  confirmClass,
  onConfirm,
  onClose,
}: {
  heading: string;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: (notes: string) => Promise<void>;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    setLoading(true);
    await onConfirm(notes);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 px-4" onClick={onClose}>
      <div className="bg-card border border-input rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground">{heading}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Admin notes (optional)"
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Cancel</button>
          <button
            onClick={handle}
            disabled={loading}
            className={cn('px-4 py-2 text-sm font-medium rounded-lg text-primary-foreground disabled:opacity-50 flex items-center gap-1.5', confirmClass)}
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Expandable note cell ──
function NoteCell({ text }: { text: string | null }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <button onClick={() => setExpanded(v => !v)} className="text-left text-sm text-foreground">
      <span className={cn(!expanded && 'line-clamp-2')}>{text}</span>
    </button>
  );
}

// ── Main page ──
function SuggestionsPage() {
  const [tab, setTab] = useState<TabFilter>('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FetchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<Record<TabFilter, number>>({ all: 0, pending: 0, approved: 0, rejected: 0 });

  // Modal state
  const [modal, setModal] = useState<{ id: string; action: 'approved' | 'rejected'; placeName?: string } | null>(null);

  // Drawer state
  const [drawer, setDrawer] = useState<{ googlePlaceId: string; suggestionId: string; status: Status } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (tab !== 'all') params.set('status', tab);
    try {
      const res = await fetch(`${API_BASE}/admin/suggestions?${params}`);
      const json: FetchResult = await res.json();
      setData(json);
    } catch {
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  // Fetch counts for badges
  const fetchCounts = useCallback(async () => {
    try {
      const [all, pending, approved, rejected] = await Promise.all([
        fetch(`${API_BASE}/admin/suggestions?page=1&page_size=1`).then(r => r.json()),
        fetch(`${API_BASE}/admin/suggestions?page=1&page_size=1&status=pending`).then(r => r.json()),
        fetch(`${API_BASE}/admin/suggestions?page=1&page_size=1&status=approved`).then(r => r.json()),
        fetch(`${API_BASE}/admin/suggestions?page=1&page_size=1&status=rejected`).then(r => r.json()),
      ]);
      setCounts({ all: all.total, pending: pending.total, approved: approved.total, rejected: rejected.total });
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleReject = async (id: string, adminNotes: string) => {
    const prev = data;
    if (data) {
      setData({
        ...data,
        suggestions: data.suggestions.map(s => s.id === id ? { ...s, status: 'rejected' as Status, admin_notes: adminNotes || s.admin_notes } : s),
      });
    }
    setModal(null);
    try {
      const res = await fetch(`${API_BASE}/admin/suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected', admin_notes: adminNotes || undefined }),
      });
      if (!res.ok) throw new Error();
      toast.success('Suggestion rejected');
      fetchCounts();
    } catch {
      toast.error('Action failed');
      if (prev) setData(prev);
    }
  };

  const handleApprove = async (id: string, payload: ApprovePayload): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE}/admin/suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return { ok: false, error: body.detail || body.error || 'Something went wrong. Please try again.' };
      }
      // Success — update row optimistically
      if (data) {
        setData({
          ...data,
          suggestions: data.suggestions.map(s => s.id === id ? { ...s, status: 'approved' as Status, admin_notes: payload.admin_notes || s.admin_notes } : s),
        });
      }
      setModal(null);
      toast.success('Spot approved and added to Qwip!');
      fetchCounts();
      return { ok: true };
    } catch {
      return { ok: false, error: 'Network error. Please try again.' };
    }
  };

  const suggestions = data?.suggestions || [];
  const total = data?.total || 0;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <h1 className="text-2xl font-bold text-foreground mb-5">Spot Suggestions</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.value}
              onClick={() => { setTab(t.value); setPage(1); }}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5',
                tab === t.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              <span className={cn(
                'text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center',
                tab === t.value ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-background text-muted-foreground',
              )}>
                {counts[t.value]}
              </span>
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {/* Empty */}
        {!loading && suggestions.length === 0 && (
          <div className="text-center py-16">
            <p className="text-foreground font-semibold">No {tab === 'all' ? '' : tab} suggestions</p>
            <p className="text-muted-foreground text-sm mt-1">
              {tab === 'pending' ? 'All caught up!' : tab === 'all' ? 'No suggestions submitted yet.' : `No ${tab} suggestions yet.`}
            </p>
          </div>
        )}

        {/* Table */}
        {!loading && suggestions.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block border border-input rounded-2xl overflow-hidden bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-input bg-muted/50">
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Place</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Submitted by</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Note</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Submitted</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map(s => (
                    <tr key={s.id} className="border-b border-input last:border-b-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-foreground">{s.place_name}</div>
                        {s.place_address && <div className="text-xs text-muted-foreground mt-0.5">{s.place_address}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-foreground">{s.suggester_name || 'Anonymous'}</div>
                        {s.suggester_email && <div className="text-xs text-muted-foreground mt-0.5">{s.suggester_email}</div>}
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <NoteCell text={s.note} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{relativeTime(s.created_at)}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setDrawer({ googlePlaceId: s.google_place_id, suggestionId: s.id, status: s.status })} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-input text-foreground hover:bg-muted inline-flex items-center gap-1">
                            <Eye size={13} /> View
                          </button>
                          {s.status === 'pending' ? (
                            <>
                              <button onClick={() => setModal({ id: s.id, action: 'approved', placeName: s.place_name })} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-success text-primary-foreground hover:opacity-90">Approve</button>
                              <button onClick={() => setModal({ id: s.id, action: 'rejected', placeName: s.place_name })} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-destructive text-destructive hover:bg-destructive/10">Reject</button>
                            </>
                          ) : (
                            <StatusBadge status={s.status} />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {suggestions.map(s => (
                <div key={s.id} className="border border-input rounded-xl bg-card p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-foreground">{s.place_name}</div>
                      {s.place_address && <div className="text-xs text-muted-foreground">{s.place_address}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setDrawer({ googlePlaceId: s.google_place_id, suggestionId: s.id, status: s.status })} className="px-2.5 py-1 text-xs font-medium rounded-lg border border-input text-foreground hover:bg-muted inline-flex items-center gap-1">
                        <Eye size={12} /> View
                      </button>
                      <StatusBadge status={s.status} />
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {s.suggester_name || 'Anonymous'}{s.suggester_email ? ` · ${s.suggester_email}` : ''} · {relativeTime(s.created_at)}
                  </div>
                  {s.note && <NoteCell text={s.note} />}
                  {s.status === 'pending' && (
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setModal({ id: s.id, action: 'approved', placeName: s.place_name })} className="flex-1 py-2 text-xs font-medium rounded-lg bg-success text-primary-foreground">Approve</button>
                      <button onClick={() => setModal({ id: s.id, action: 'rejected', placeName: s.place_name })} className="flex-1 py-2 text-xs font-medium rounded-lg border border-destructive text-destructive">Reject</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 text-sm">
                <span className="text-muted-foreground">Showing {start}–{end} of {total}</span>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg border border-input text-foreground disabled:opacity-40">Prev</button>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg border border-input text-foreground disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Action modal */}
      {modal && modal.action === 'rejected' && (
        <ActionModal
          heading="Reject suggestion"
          confirmLabel="Reject"
          confirmClass="bg-destructive"
          onConfirm={notes => handleReject(modal.id, notes)}
          onClose={() => setModal(null)}
        />
      )}
      {modal && modal.action === 'approved' && (
        <ApproveModal
          placeName={modal.placeName || ''}
          onConfirm={payload => handleApprove(modal.id, payload)}
          onClose={() => setModal(null)}
        />
      )}
      {/* Place detail drawer */}
      <PlaceDetailDrawer
        googlePlaceId={drawer?.googlePlaceId || null}
        suggestionId={drawer?.suggestionId || null}
        status={drawer?.status || null}
        onClose={() => setDrawer(null)}
        onAction={(id, action) => {
          const s = suggestions.find(sg => sg.id === id);
          setDrawer(null);
          setModal({ id, action, placeName: s?.place_name });
        }}
      />
    </div>
  );
}

export default function SuggestionsAdminProtected() {
  return (
    <AdminAuthGate>
      <SuggestionsPage />
    </AdminAuthGate>
  );
}
